import { ArrowLeft, CaretLeft, X } from "@phosphor-icons/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { clsx } from "clsx";
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
} from "react";

import { Button } from "common/components/ui/Button";

interface DialogProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Root> {
  onAfterClose?: () => void;
}

const dialogCloseDuration = 300;

function Dialog({ onAfterClose = null, onOpenChange, ...props }: DialogProps) {
  return (
    <DialogPrimitive.Root
      onOpenChange={(open) => {
        if (onOpenChange) {
          onOpenChange(open);
        }
        if (!open && onAfterClose) {
          setTimeout(() => onAfterClose(), dialogCloseDuration);
        }
      }}
      {...props}
    />
  );
}
Dialog.displayName = DialogPrimitive.Root.displayName;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = DialogPrimitive.Overlay;

const dialogWidthClassNames = {
  md: "w-112",
  lg: "w-135",
  xl: "w-225",
} as const;

interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  size?: keyof typeof dialogWidthClassNames;
}

function isThirdPartyModalOpen() {
  return !!document.getElementById("Cardinal-ElementContainer");
}

const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      size = "md",
      onInteractOutside,
      onEscapeKeyDown,
      ...props
    },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay
        onClick={(e) => e.stopPropagation()}
        className="fixed inset-0 z-overlay bg-black-950/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      />
      <DialogPrimitive.Content
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        className={clsx(
          "transition-none max-h-9/10 fixed overflow-auto flex flex-col left-1/2 top-1/2 z-dialog -translate-x-1/2 -translate-y-1/2 bg-white shadow-lg duration-200 focus:outline-none",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          className,
          dialogWidthClassNames[size],
          size === "xl" ? "rounded-4xl" : "rounded-5xl",
        )}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.target as HTMLDivElement).focus();
        }}
        onInteractOutside={(e) => {
          if (isThirdPartyModalOpen()) e.preventDefault();

          onInteractOutside?.(e);
        }}
        onEscapeKeyDown={(e) => {
          if (isThirdPartyModalOpen()) e.preventDefault();

          onEscapeKeyDown?.(e);
        }}
        {...props}
      >
        {children}
        <Button asChild size="sm" variant="ghost-black" intent="iconOnly">
          <DialogClose
            id="close-button"
            className={clsx(
              "absolute",
              size === "xl" ? "top-2 right-2" : "right-4 top-4",
            )}
          >
            <X />
            <span className="sr-only">Close</span>
          </DialogClose>
        </Button>
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const OverlayDialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogPrimitive.Overlay />
    <DialogPrimitive.Content
      ref={ref}
      className={clsx(
        "fixed flex flex-col inset-x-0 top-0 z-dialog bg-white duration-200 focus:outline-none h-dvh overflow-y-auto",
        className,
      )}
      onOpenAutoFocus={(e) => {
        e.preventDefault();
        (e.target as HTMLDivElement).focus();
      }}
      {...props}
    >
      {children}
      <Button asChild variant="secondary-black" intent="iconOnly">
        <DialogClose id="close-button" className="absolute left-4 top-3">
          <CaretLeft size={20} />
          <span className="sr-only">Close</span>
        </DialogClose>
      </Button>
    </DialogPrimitive.Content>
  </DialogPortal>
));
OverlayDialogContent.displayName = "OverlayDialogContent";

function OverlayDialogTitle({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <DialogPrimitive.Title
      className={clsx("px-14 py-4 text-center text-xl-bold", className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Title>
  );
}
function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx("flex p-4 rounded-b-3xl gap-x-2.5", className)}
      {...props}
    />
  );
}
DialogFooter.displayName = "DialogFooter";

function DialogTitle({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <DialogPrimitive.Title
      className={clsx("wrap-break-word first-letter:uppercase", className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Title>
  );
}

function DialogDescription({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <DialogPrimitive.Description
      className={clsx("text-sm text-black-500", className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Description>
  );
}

function DialogBody({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx(
        "flex flex-col p-4 overflow-y-auto scrollbar-thin",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

type DialogHeaderProps = (
  | ({ variant: "default"; subtitle?: ReactNode } & (
      | { onBack?: () => void }
      | { imgSrc?: string }
    ))
  | { variant?: "border" }
) & { title: ReactNode };

function DialogHeader({
  title,
  variant = "default",
  ...rest
}: DialogHeaderProps) {
  let backButton: JSX.Element;

  if (variant === "default" && "onBack" in rest && rest.onBack) {
    const { onBack } = rest;
    backButton = (
      <Button
        size="sm"
        variant="ghost-black"
        intent="iconOnly"
        onClick={onBack}
        className={clsx(!onBack && "invisible")}
        tabIndex={onBack ? 0 : -1}
      >
        <ArrowLeft />
        <span className="sr-only">Back</span>
      </Button>
    );
  }
  return (
    <div
      className={clsx(
        "flex flex-col pr-12",
        variant === "border"
          ? "border-b border-b-black-100 py-3.5 px-4 text-md-bold"
          : "py-4 text-xl-bold",
        variant !== "border" && (backButton ? "pl-4" : "pl-12"),
      )}
    >
      {"imgSrc" in rest && (
        <img
          src={rest.imgSrc}
          width={280}
          height={195}
          alt=""
          className="mx-auto"
        />
      )}
      <div className="flex items-start">
        {backButton}

        <div className="flex flex-col">
          <DialogTitle
            className={clsx(
              "flex-1 overflow-hidden",
              variant === "default" && "text-center mt-1",
            )}
          >
            {title}
          </DialogTitle>
          {"subtitle" in rest && (
            <DialogDescription className="mt-2 text-center">
              {rest.subtitle}
            </DialogDescription>
          )}
        </div>
      </div>
    </div>
  );
}
DialogHeader.displayName = "DialogHeader";

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogClose,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogHeader,
  DialogBody,
  OverlayDialogContent,
  OverlayDialogTitle,
};
